import { AuthResponse } from '../../types.ts';
import type { Korting, OpzegReden } from '../../utils/korting.ts';
import {
  fetchWithTimeout,
  parseErrorResponse,
  ApiError,
  REGISTER_TIMEOUT,
  BULK_FETCH_TIMEOUT,
  startProactiveRefresh,
  stopProactiveRefresh,
  tryRefreshToken,
} from './base.ts';

/**
 * De klik-id van een Google Ads-advertentie, meegereisd vanaf pecktree.nl.
 *
 * Staat hier en niet in het registratieformulier, omdat er meerdere formulieren
 * op deze ene call uitkomen (de gewone registratie en de mini-versie bij een
 * organisatie-uitnodiging). Eén plek waar ze allemaal langskomen is een kleinere
 * ingreep dan dezelfde regel in elk formulier — en er komt er ooit nog een bij.
 *
 * Alleen de URL, bewust geen opslag: alles wat op het apparaat wordt gezet valt
 * onder artikel 11.7a Telecommunicatiewet en zou toestemming vragen. De id komt
 * binnen op /register?gclid=… en wordt op datzelfde scherm verzilverd.
 */
export function readGclid(): string | undefined {
  try {
    const gclid = new URLSearchParams(window.location.search).get('gclid');
    return gclid && /^[A-Za-z0-9_-]{1,255}$/.test(gclid) ? gclid : undefined;
  } catch {
    // Een gemiste meting, geen mislukte registratie.
    return undefined;
  }
}

/**
 * Fout zonder servertekst (#1773): de Engelse terugval blijft de `message`
 * (logs), en een i18n-sleutel laat het scherm vertalen via `messageKey`
 * (AuthScreen `errorText`, `getErrorMessage`). Geeft de server zelf een
 * melding of sleutel mee, dan blijft die leidend.
 */
function authError(
  data: Record<string, unknown>,
  status: number,
  fallback: string,
  messageKey: string
): ApiError {
  const own = data.error || data.messageKey;
  return new ApiError(String(data.error || fallback), status, own ? data : { ...data, messageKey });
}

export const authApi = {
  // Expose tryRefreshToken for "stay logged in" button
  tryRefreshToken,

  // --- AUTH ---
  login: async (credentials: {
    email: string;
    password?: string;
  }): Promise<AuthResponse & { requires2FA?: boolean }> => {
    const res = await fetchWithTimeout('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Login failed', 'loginFailed');
    // If 2FA is required, return the temp token without caching user
    if (data.requires2FA) return data;
    // Token is set as httpOnly cookie by the backend — only cache user profile
    localStorage.setItem('af_user', JSON.stringify(data.user));
    startProactiveRefresh();
    return data;
  },

  // tempToken zit in de httpOnly two_factor_token-cookie (gezet bij login) en gaat
  // automatisch mee; de client kent/verstuurt hem niet meer (XSS-mitigatie).
  validate2FA: async (code: string): Promise<AuthResponse> => {
    const res = await fetchWithTimeout('/api/auth/2fa/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, '2FA validation failed', 'ogErr2faValidate');
    localStorage.setItem('af_user', JSON.stringify(data.user));
    startProactiveRefresh();
    return data;
  },

  setup2FA: async (): Promise<{ secret: string; uri: string }> => {
    const res = await fetchWithTimeout('/api/auth/2fa/setup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, '2FA setup failed', 'ogErr2faSetup');
    return data;
  },

  verify2FA: async (code: string): Promise<{ backupCodes: string[] }> => {
    const res = await fetchWithTimeout('/api/auth/2fa/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, '2FA verification failed', 'ogErr2faVerify');
    return data;
  },

  disable2FA: async (password: string): Promise<void> => {
    const res = await fetchWithTimeout('/api/auth/2fa/disable', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, '2FA disable failed', 'ogErr2faDisable');
  },

  regenerate2FACodes: async (password: string): Promise<{ backupCodes: string[] }> => {
    const res = await fetchWithTimeout('/api/auth/2fa/regenerate-codes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    const data = await res.json();
    if (!res.ok)
      throw authError(data, res.status, 'Regenerate codes failed', 'ogErrRegenerateCodes');
    return data;
  },

  register: async (credentials: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    invitationToken?: string;
    /** #1709 — taal waarin het aanmeldscherm stond; wordt preferred_language. */
    language?: string;
  }): Promise<{ success: boolean; emailQueued: boolean }> => {
    const res = await fetchWithTimeout(
      '/api/auth/register',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // JSON.stringify laat een undefined gclid gewoon weg.
        body: JSON.stringify({ ...credentials, gclid: readGclid() }),
      },
      // #1402 — registratie hasht een wachtwoord met bcrypt cost 12 en raakt de
      // database een paar keer. De standaard van 5 s liet de browser afbreken
      // met een rauwe "aborted", terwijl het account al was aangemaakt.
      REGISTER_TIMEOUT
    );
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Registration failed', 'ogRegistrationFailed');
    return data;
  },

  /**
   * Vraag een nieuwe activatiemail aan (#1403).
   *
   * De server antwoordt altijd hetzelfde, ook voor een onbekend adres — dat is
   * opzet, niet een gebrek aan terugkoppeling.
   */
  resendVerification: async (email: string): Promise<{ success: boolean }> => {
    const res = await fetchWithTimeout(
      '/api/auth/resend-verification',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      },
      REGISTER_TIMEOUT
    );
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Resend failed', 'ogErrResend');
    return data;
  },

  loginWithGoogle: async (
    credential: string,
    // #1709 — taal van het aanmeldscherm; telt alleen als dit een nieuw account wordt.
    language?: string
  ): Promise<AuthResponse & { requires2FA?: boolean }> => {
    const res = await fetchWithTimeout('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential, language }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Google login failed', 'ogErrGoogleLogin');
    if (data.requires2FA) return data;
    localStorage.setItem('af_user', JSON.stringify(data.user));
    startProactiveRefresh();
    return data;
  },

  logout: async () => {
    // #466: zet intent-flag voordat we fetch doen — zelfs als de server-call
    // faalt (bv. netwerkvertraging na laptop-sleep), kan App.init() dan bij
    // de volgende F5 detecteren dat er een logout-intent is en de silent
    // refresh-flow overslaan. Voorkomt dat F5 een "uitgelogde" user
    // automatisch weer inlogt via nog-geldige refresh-token cookies.
    try {
      localStorage.setItem('af_intent_logged_out', '1');
    } catch {
      /* storage full / sandbox */
    }
    // #466 multi-tab: broadcast naar andere open tabs zodat die ook uitloggen.
    // De useInactivityTimeout-hook luistert op deze channel en voert lokale
    // cleanup + reload uit (zonder opnieuw ApiService.logout() aan te roepen
    // — dat zou recursie veroorzaken).
    try {
      const ch = new BroadcastChannel('pecktree-inactivity');
      ch.postMessage({ type: 'logout' });
      ch.close();
    } catch {
      /* BroadcastChannel niet ondersteund — andere tabs merken het op via 401 */
    }
    // Call backend to clear httpOnly cookie
    try {
      await fetchWithTimeout('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      stopProactiveRefresh();
      localStorage.removeItem('af_user');
      localStorage.removeItem('af_current_project');
    }
  },

  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const res = await fetchWithTimeout('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Request failed', 'ogErrRequest');
    return data;
  },

  resetPassword: async (
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    const res = await fetchWithTimeout('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Password reset failed', 'ogErrPasswordReset');
    return data;
  },

  // #72 (ultra-UX-audit) — wachtwoord wijzigen in-app. De backend valideert het
  // huidige wachtwoord, controleert de sterkte, en revoket de sessie server-side
  // ("opnieuw inloggen"). De ApiError draagt data.messageKey door zodat
  // getErrorMessage de juiste foutmelding toont (verkeerd huidig wachtwoord /
  // te zwak). Na succes roept de caller ApiService.logout() aan voor de
  // volledige client-uitlog (intent-flag + refresh-cookie + broadcast).
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    const res = await fetchWithTimeout('/api/auth/password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Password change failed', 'ogErrPasswordChange');
    return data;
  },

  /**
   * Vraag een wijziging van het e-mailadres aan. Het adres verandert pas als
   * de link in het NIEUWE postvak wordt aangeklikt; tot die tijd log je in met
   * je huidige adres. Het oude adres krijgt een melding dat er een wijziging
   * loopt (toegevoegd 2026-09-01).
   */
  requestEmailChange: async (
    newEmail: string,
    password: string
  ): Promise<{ success: boolean; pending_email: string; email_sent: boolean }> => {
    const res = await fetchWithTimeout('/api/account/email-change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newEmail, password }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Email change failed', 'ogErrEmailChange');
    return data;
  },

  cancelEmailChange: async (): Promise<void> => {
    const res = await fetchWithTimeout('/api/account/email-change', { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw authError(data, res.status, 'Cancel failed', 'ogErrCancel');
    }
  },

  updatePreferredLanguage: async (preferred_language: string): Promise<void> => {
    const res = await fetchWithTimeout('/api/auth/profile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preferred_language }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Failed to update language', 'ogErrLanguage');
    // Update cached user data
    localStorage.setItem('af_user', JSON.stringify(data.user));
  },

  setLastProject: async (projectId: string): Promise<void> => {
    try {
      await fetchWithTimeout('/api/account/last-project', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
    } catch {
      // Non-critical — silently ignore network errors
    }
  },

  updateProfile: async (data: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
  }): Promise<void> => {
    const res = await fetchWithTimeout('/api/account/profile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  uploadAccountAvatar: async (
    fileData: string,
    mimeType: string
  ): Promise<{ avatarUrl: string }> => {
    const res = await fetchWithTimeout('/api/account/avatar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileData, mime_type: mimeType }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  deleteAccountAvatar: async (): Promise<void> => {
    const res = await fetchWithTimeout('/api/account/avatar', {
      method: 'DELETE',
    });
    if (!res.ok) await parseErrorResponse(res);
  },

  getUserUsage: async (): Promise<{
    projects: number;
    maxProjects: number;
    people: number;
    maxPeople: number;
    storageUsed: number;
    maxStorage: number;
    mediaStorage: number;
    maxMediaStorage: number;
    tier: string;
  }> => {
    const res = await fetchWithTimeout('/api/auth/usage');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // #1730 — prijzen voor de abonnementskaarten (publiek, met 5 min cache).
  getPricing: async (): Promise<{
    currency: string;
    pricing: Record<'pro' | 'business' | 'bureau', { monthly: number; annual: number }>;
  }> => {
    const res = await fetchWithTimeout('/api/config/pricing');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // Stripe / Subscription
  createStripeSession: async (
    tier: string,
    interval: 'monthly' | 'yearly' = 'monthly',
    kortingscode?: string
  ): Promise<{ url: string }> => {
    const res = await fetchWithTimeout('/api/checkout/create-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier, interval, ...(kortingscode ? { kortingscode } : {}) }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  verifyCheckoutSession: async (
    sessionId: string
  ): Promise<{ status: string; payment_status: string; tier: string }> => {
    const res = await fetchWithTimeout(
      `/api/checkout/verify-session?session_id=${encodeURIComponent(sessionId)}`
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  getSubscription: async (): Promise<{
    tier: string;
    subscription_status: string;
    subscription_interval: string | null;
    subscription_current_period_end: string | null;
    subscription_cancel_at?: string | null;
    subscription_started_at?: string | null;
    has_subscription?: boolean;
  }> => {
    const res = await fetchWithTimeout('/api/subscription/me');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // #1854 (B2) — overstappen binnen het lopende abonnement, geen tweede afrekening.
  changePlan: async (
    tier: string,
    interval: 'monthly' | 'yearly'
  ): Promise<{ tier: string; interval: string }> => {
    const res = await fetchWithTimeout(
      '/api/subscription/change-plan',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier, interval }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // #1915 — bestaat deze kortingscode, en voor welk plan? Zonder plan: alleen of hij bestaat.
  controleerKortingscode: async (
    code: string,
    plan?: 'pro' | 'business' | 'bureau',
    interval?: 'monthly' | 'yearly' | 'annual'
  ): Promise<{ korting: Korting }> => {
    const res = await fetchWithTimeout('/api/kortingscode/controleer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, plan, interval }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // #1916 — opzeggen per einde periode, vanuit Pecktree zelf.
  cancelSubscription: async (
    reden: OpzegReden | null
  ): Promise<{ cancelAt: number | null; terugbetaaldCents: number }> => {
    // #1919 — kan terugbetalen: meerdere Stripe-aanroepen na elkaar, dus de ruime limiet.
    const res = await fetchWithTimeout(
      '/api/subscription/cancel',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reden }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // #1919 — wat opzeggen nu doet (na een verlenging: over een maand, geld terug) en wat
  // intrekken kost als er al is terugbetaald.
  getOpzegVoorbeeld: async (): Promise<{
    naVerlenging: boolean;
    stoptOp: number | null;
    terugCents: number;
    intrekkenKostCents: number;
  }> => {
    const res = await fetchWithTimeout('/api/subscription/opzegvoorbeeld', {}, BULK_FETCH_TIMEOUT);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // #1854 (B7) — een opzegging intrekken. #1919: betaal = het teruggekregen bedrag weer betalen.
  resumeSubscription: async (
    betaal = false
  ): Promise<{ resumed: boolean; betaaldCents: number }> => {
    // Kan een losse factuur maken en betalen: de ruime limiet.
    const res = await fetchWithTimeout(
      '/api/subscription/resume',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ betaal }),
      },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  // #1855 (B16) — herroepen binnen de bedenktijd: direct stoppen en alles terug.
  withdrawSubscription: async (): Promise<{
    withdrawn: boolean;
    refundedCents: number;
    currency: string;
  }> => {
    const res = await fetchWithTimeout(
      '/api/subscription/withdraw',
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  createPortalSession: async (): Promise<{ url: string }> => {
    const res = await fetchWithTimeout('/api/subscription/portal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  redeemCoupon: async (
    code: string
  ): Promise<{
    user: Record<string, unknown>;
    coupon: Record<string, unknown>;
    message: string;
  }> => {
    const res = await fetchWithTimeout('/api/coupons/redeem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    if (!res.ok) throw authError(data, res.status, 'Coupon redemption failed', 'ogErrCoupon');
    // Update cached user with new tier/trial info
    if (data.user) localStorage.setItem('af_user', JSON.stringify(data.user));
    return data;
  },

  // GDPR: Export all user data
  exportAccountData: async (): Promise<any> => {
    const res = await fetchWithTimeout('/api/account/export');
    if (!res.ok) await parseErrorResponse(res);

    // Trigger download
    const data = await res.json();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pecktree-export-${Date.now()}.json`;
    a.click();
    window.URL.revokeObjectURL(url);

    return data;
  },

  // GDPR: Delete account and all data
  deleteAccount: async (confirmation: string): Promise<void> => {
    if (confirmation !== 'DELETE') {
      throw new Error('Account deletion requires confirmation');
    }

    const res = await fetchWithTimeout('/api/account', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ confirmation }),
    });

    if (!res.ok) await parseErrorResponse(res);

    // Clear local storage and redirect
    localStorage.clear();
    window.location.href = '/';
  },
};
